import {
    AttachMoney as DollarIcon,
    Description as FileTextIcon,
    Store as ShopIcon,
    Person as UserIcon,
    Dashboard as DashboardIcon,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Typography,
    Button,
    Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeaderBanner from '../components/PageHeaderBanner';
import { useApi } from '../contexts/ApiContext';

interface StatsOverview {
    totalRevenue: number;
    totalClients: number;
    activeContracts: number;
    inventoryValue: number;
}

// Custom Statistic Component for MUI
interface StatisticProps {
    title: string;
    value: number;
    prefix?: React.ReactNode;
    suffix?: string;
    precision?: number;
    valueStyle?: React.CSSProperties;
}

const Statistic = ({ title, value, prefix, suffix, precision, valueStyle }: StatisticProps) => {
    const accent = (valueStyle?.color as string) ?? '#1976d2';
    return (
        <Stack direction="row" spacing={1.5} alignItems="center">
            {prefix && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 46,
                        height: 46,
                        borderRadius: 2,
                        color: accent,
                        backgroundColor: alpha(accent, 0.12),
                        '& svg': { fontSize: 26 },
                    }}
                >
                    {prefix}
                </Box>
            )}
            <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2, ...valueStyle }}>
                    {precision !== undefined ? value.toFixed(precision) : value}
                    {suffix && ` ${suffix}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {title}
                </Typography>
            </Box>
        </Stack>
    );
};

const Dashboard = () => {
    const [stats, setStats] = useState<StatsOverview | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { stats: statsApi } = useApi();
    const { t } = useTranslation();

    const loadDashboardData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const result = await statsApi.getOverview();

            if (result.success && result.data) {
                setStats(result.data);
            } else {
                setError(result.error ?? 'Failed to load dashboard data');
            }
        } catch (err) {
            setError('Network error occurred');
            console.error('Dashboard load error:', err);
        } finally {
            setLoading(false);
        }
    }, [statsApi]);

    useEffect(() => {
        loadDashboardData();
    }, [loadDashboardData]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert
                severity="error"
                action={
                    <Button color="inherit" size="small" onClick={loadDashboardData}>
                        Retry
                    </Button>
                }
            >
                <Typography variant="subtitle1">Error Loading Dashboard</Typography>
                <Typography variant="body2">{error}</Typography>
            </Alert>
        );
    }

    // Default stats if API doesn't return data
    const defaultStats: StatsOverview = {
        totalClients: 0,
        activeContracts: 0,
        inventoryValue: 0,
        totalRevenue: 0,
    };

    const dashboardStats = stats ?? defaultStats;

    return (
        <Box>
            <PageHeaderBanner
                icon={<DashboardIcon />}
                title={t('dashboard.title')}
                subtitle={t('dashboard.subtitle')}
                accentColor="#1976d2"
            />

            <Box
                display="grid"
                gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))"
                gap={2}
                sx={{ marginBottom: 3 }}
            >
                <Card
                    sx={{
                        borderTop: '3px solid #3f8600',
                        transition: 'box-shadow 0.2s, transform 0.2s',
                        '&:hover': { boxShadow: 6, transform: 'translateY(-2px)' },
                    }}
                >
                    <CardContent>
                        <Statistic
                            title={t('dashboard.totalClients')}
                            value={dashboardStats.totalClients}
                            prefix={<UserIcon />}
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </CardContent>
                </Card>

                <Card
                    sx={{
                        borderTop: '3px solid #1890ff',
                        transition: 'box-shadow 0.2s, transform 0.2s',
                        '&:hover': { boxShadow: 6, transform: 'translateY(-2px)' },
                    }}
                >
                    <CardContent>
                        <Statistic
                            title={t('dashboard.activeContracts')}
                            value={dashboardStats.activeContracts}
                            prefix={<FileTextIcon />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </CardContent>
                </Card>

                <Card
                    sx={{
                        borderTop: '3px solid #722ed1',
                        transition: 'box-shadow 0.2s, transform 0.2s',
                        '&:hover': { boxShadow: 6, transform: 'translateY(-2px)' },
                    }}
                >
                    <CardContent>
                        <Statistic
                            title={t('dashboard.inventoryItems')}
                            value={dashboardStats.inventoryValue}
                            prefix={<ShopIcon />}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </CardContent>
                </Card>

                <Card
                    sx={{
                        borderTop: '3px solid #cf1322',
                        transition: 'box-shadow 0.2s, transform 0.2s',
                        '&:hover': { boxShadow: 6, transform: 'translateY(-2px)' },
                    }}
                >
                    <CardContent>
                        <Statistic
                            title={t('dashboard.monthlyRevenue')}
                            value={dashboardStats.totalRevenue}
                            prefix={<DollarIcon />}
                            suffix="LEI"
                            precision={2}
                            valueStyle={{ color: '#cf1322' }}
                        />
                    </CardContent>
                </Card>
            </Box>

            <Box display="grid" gridTemplateColumns={{ xs: '1fr', lg: '1fr 1fr' }} gap={2}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {t('dashboard.recentActivity')}
                        </Typography>
                        <Box sx={{ padding: '16px 0' }}>
                            <Typography variant="body2">
                                - New client registered: John Doe
                            </Typography>
                            <Typography variant="body2">- Contract #1234 expired</Typography>
                            <Typography variant="body2">
                                - Payment received for contract #5678
                            </Typography>
                            <Typography variant="body2">
                                - Inventory item added: Gold Ring
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {t('dashboard.quickActions')}
                        </Typography>
                        <Box sx={{ padding: '16px 0' }}>
                            <Typography variant="body2">
                                - <a href="/clients">Add New Client</a>
                            </Typography>
                            <Typography variant="body2">
                                - <a href="/contracts">Create Contract</a>
                            </Typography>
                            <Typography variant="body2">
                                - <a href="/inventory">Manage Inventory</a>
                            </Typography>
                            <Typography variant="body2">
                                - <a href="/reports">Generate Reports</a>
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default Dashboard;
